import { getTipImage } from '../brush/library';
import type { ChannelViewOptions } from '../core/channels';
import { findLayerById, surfaceLayerIds } from '../core/layerTree';
import type { Mat3 } from '../core/math';
import { blendIfIsDefault } from '../core/blendIf';
import { hasActiveEffects } from '../core/layerStyles';
import type { BlendChannels, DocumentMeta, GroupLayer, Layer, Rect, ViewState } from '../core/types';
import { DEFAULT_BLEND_CHANNELS } from '../core/types';
import { createGLContext, type GLCapabilities, type GLContext } from '../gl/context';
import { Renderer } from '../gl/renderer';
import { RenderTarget } from '../gl/renderTarget';
import { invalidateReadFramebuffer, Texture } from '../gl/texture';
import {
  buildViewMatrix,
  composeSrcMatrices,
  contentOffsetSrcMatrix,
  type CanvasSize,
} from '../gl/view';
import { adjustmentPass } from './adjustPass';
import { renderFillLayer } from './fillPass';
import type { EngineHistoryOp, FullSnapshot } from './historyOps';
import { applyLayerEffects } from './layerFxPass';
import { DocumentResources, type ResourceSnapshot } from './resources';
import { filterPass } from './filterPass';

const CHECKER_SIZE_CSS = 8;
const ANTS_DASH_CSS = 4;
/** `uMode` of `combine.frag` that multiplies two coverage masks. */
const INTERSECT_MODE = 3;
/** `uGrey` of `channelView.frag` that shows the overlay texture as greyscale. */
const GREY_OVERLAY = 3;

interface CompositeContext {
  doc: DocumentMeta;
  resources: DocumentResources;
  options: RenderOptions;
}

/** A composited texture together with the pooled buffer that backs it, if any. */
interface CompositeResult {
  texture: Texture;
  owner: RenderTarget | null;
}

function blendChannelsAreDefault(channels: BlendChannels | undefined): boolean {
  const c = channels ?? DEFAULT_BLEND_CHANNELS;
  return c.r && c.g && c.b && c.a;
}

/**
 * A plain group blends its children straight into the parent's backdrop. Opacity,
 * a mask, channel restrict, effects or a blend mode of its own force the group
 * to be composited in isolation first, because those cannot be expressed per child.
 */
function isPassThrough(layer: Layer): layer is GroupLayer {
  if (layer.kind !== 'group') return false;
  const hasMask = layer.hasMask && layer.maskEnabled;
  return (
    layer.blendMode === 'normal' &&
    layer.opacity >= 1 &&
    !hasMask &&
    blendChannelsAreDefault(layer.blendChannels) &&
    blendIfIsDefault(layer.blendIf) &&
    !hasActiveEffects(layer.effects)
  );
}

export interface RenderOptions {
  view: ViewState;
  /** Textures that stand in for a layer's own pixels (stroke / adjustment previews). */
  overrides?: ReadonlyMap<string, Texture> | null;
  /** Textures that stand in for a layer's mask while a stroke paints into it. */
  maskOverrides?: ReadonlyMap<string, Texture> | null;
  /** Textures that stand in for an alpha channel while a stroke paints into it. */
  channelOverrides?: ReadonlyMap<string, Texture> | null;
  /** Live move or free-transform preview for a single layer. */
  transform?: { layerId: string; srcMatrix: Mat3 } | null;
  /** Transform Selection preview, applied to the outline only. */
  selectionTransform?: Mat3 | null;
  showSelection?: boolean;
  antsPhase?: number;
  /** Non-composite channel view; `null` or absent draws the plain composite. */
  channels?: ChannelViewOptions | null;
}

export interface EngineCallbacks {
  onMetaRestore?: (docId: string, meta: DocumentMeta) => void;
  onContextLost?: () => void;
  onContextRestored?: () => void;
}

/**
 * Owns the single WebGL2 context and all per-document GPU resources. The React
 * store holds only metadata and calls into here; nothing in this class reads
 * component state.
 */
export class Engine {
  callbacks: EngineCallbacks = {};

  private ctx: GLContext | null = null;
  private canvasEl: HTMLCanvasElement | null = null;
  private rendererInstance: Renderer | null = null;
  private readonly docs = new Map<string, DocumentResources>();
  private frameHandle = 0;
  private frameCallback: (() => void) | null = null;
  private cssWidth = 1;
  private cssHeight = 1;
  private dpr = 1;
  private compositeDirty = true;
  private compositeCache: { docId: string; texture: Texture } | null = null;
  private thumbTarget: RenderTarget | null = null;
  private coverageThumbTarget: RenderTarget | null = null;
  private previewTarget: RenderTarget | null = null;
  /** Sampled brush tips uploaded on demand, keyed by tip id. */
  private readonly tipTextures = new Map<string, Texture>();
  /** Adjustment lookup rows, keyed by the edit they were built from. */
  private readonly lutTextures = new Map<string, Texture>();
  /** Pattern tiles uploaded for fill layers, keyed by pattern id. */
  private readonly patternTextures = new Map<string, Texture>();

  /**
   * Binds the engine to a canvas. A hot reload hands over a new element, and a
   * context cannot follow one, so the pixels take a detour through the CPU
   * instead of being lost. History does not survive the move.
   */
  attach(canvas: HTMLCanvasElement): void {
    if (this.ctx && this.canvasEl === canvas) return;
    const carried = new Map<string, ResourceSnapshot>();
    if (this.ctx) {
      for (const [docId, resources] of this.docs) carried.set(docId, resources.snapshotPixels());
      this.detach();
    }

    this.canvasEl = canvas;
    this.ctx = createGLContext(canvas);
    this.rendererInstance = new Renderer(this.ctx.gl);
    canvas.addEventListener('webglcontextlost', this.handleContextLost);
    canvas.addEventListener('webglcontextrestored', this.handleContextRestored);

    for (const [docId, snapshot] of carried) {
      const resources = new DocumentResources(this.ctx.gl, snapshot.width, snapshot.height);
      resources.restorePixels(snapshot);
      this.docs.set(docId, resources);
    }
    if (carried.size > 0) this.invalidate();
  }

  /**
   * Builds a fresh context and renderer on the same canvas after the browser
   * dropped the old one. All GPU content is gone; callers must refill it.
   */
  reinitialize(): boolean {
    const canvas = this.canvasEl;
    if (!canvas) return false;
    this.discardResources();
    this.rendererInstance = null;
    this.ctx = null;
    try {
      this.ctx = createGLContext(canvas);
    } catch {
      return false;
    }
    if (this.ctx.gl.isContextLost()) {
      this.ctx = null;
      return false;
    }
    // The browser hands back the same context object, so per-context caches
    // still point at objects the loss destroyed.
    invalidateReadFramebuffer(this.ctx.gl);
    this.rendererInstance = new Renderer(this.ctx.gl);
    this.invalidate();
    return true;
  }

  detach(): void {
    if (this.frameHandle) cancelAnimationFrame(this.frameHandle);
    this.frameHandle = 0;
    const canvas = this.ctx?.canvas;
    canvas?.removeEventListener('webglcontextlost', this.handleContextLost);
    canvas?.removeEventListener('webglcontextrestored', this.handleContextRestored);
    this.discardResources();
    this.rendererInstance = null;
    this.ctx = null;
    this.canvasEl = null;
  }

  get available(): boolean {
    return this.ctx !== null;
  }

  get gl(): WebGL2RenderingContext {
    if (!this.ctx) throw new Error('The engine is not attached to a canvas yet');
    return this.ctx.gl;
  }

  get renderer(): Renderer {
    if (!this.rendererInstance) throw new Error('The engine is not attached to a canvas yet');
    return this.rendererInstance;
  }

  get caps(): GLCapabilities {
    if (!this.ctx) throw new Error('The engine is not attached to a canvas yet');
    return this.ctx.caps;
  }

  get canvasSize(): CanvasSize {
    return { width: this.cssWidth, height: this.cssHeight };
  }

  get devicePixelRatio(): number {
    return this.dpr;
  }

  resizeCanvas(cssWidth: number, cssHeight: number, dpr = window.devicePixelRatio || 1): void {
    if (!this.ctx) return;
    const width = Math.max(1, Math.round(cssWidth));
    const height = Math.max(1, Math.round(cssHeight));
    const ratio = Math.min(2, Math.max(1, dpr));
    if (width === this.cssWidth && height === this.cssHeight && ratio === this.dpr) return;

    this.cssWidth = width;
    this.cssHeight = height;
    this.dpr = ratio;
    const { canvas } = this.ctx;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    this.invalidate();
  }

  setFrameCallback(callback: (() => void) | null): void {
    this.frameCallback = callback;
  }

  /**
   * Coalesces redraw requests into one animation frame. `overlay` skips
   * recompositing the layer stack, which is what keeps the animated selection
   * outline from re-running the whole pipeline sixty times a second.
   */
  invalidate(kind: 'full' | 'overlay' = 'full'): void {
    if (kind === 'full') this.compositeDirty = true;
    if (this.frameHandle || !this.frameCallback) return;
    this.frameHandle = requestAnimationFrame(() => {
      this.frameHandle = 0;
      this.frameCallback?.();
    });
  }

  // --- Document resources ---

  resourcesFor(doc: DocumentMeta): DocumentResources {
    let resources = this.docs.get(doc.id);
    if (!resources) {
      resources = new DocumentResources(this.gl, doc.width, doc.height);
      this.docs.set(doc.id, resources);
    }
    if (resources.width !== doc.width || resources.height !== doc.height) {
      resources.resize(doc.width, doc.height);
    }
    for (const layerId of surfaceLayerIds(doc.layers)) resources.ensureLayer(layerId);
    return resources;
  }

  peek(docId: string): DocumentResources | undefined {
    return this.docs.get(docId);
  }

  release(docId: string): void {
    const resources = this.docs.get(docId);
    if (!resources) return;
    resources.dispose();
    this.docs.delete(docId);
    this.compositeCache = null;
    this.compositeDirty = true;
  }

  /**
   * Texture for a sampled brush tip, uploaded on first use. Tips are shared by
   * every document, so the cache hangs off the context rather than a document.
   */
  tipTexture(id: string | null): Texture | null {
    if (!id || !this.ctx) return null;
    const cached = this.tipTextures.get(id);
    if (cached) return cached;
    const image = getTipImage(id);
    if (!image) return null;

    const texture = Texture.create(this.ctx.gl, image.width, image.height, 'r8', 'linear');
    texture.uploadPixels({ x: 0, y: 0, width: image.width, height: image.height }, image.data);
    this.tipTextures.set(id, texture);
    return texture;
  }

  /**
   * Lookup row for an adjustment, uploaded once per distinct edit. An adjustment
   * layer is re-composited on every frame, so building the texture each time
   * would upload the same 1 KB row sixty times a second.
   */
  lutTexture(key: string, build: () => Uint8Array): Texture | null {
    if (!this.ctx) return null;
    const cached = this.lutTextures.get(key);
    if (cached) return cached;

    const data = build();
    const width = data.length / 4;
    const texture = Texture.create(this.ctx.gl, width, 1, 'rgba8', 'linear');
    texture.uploadPixels({ x: 0, y: 0, width, height: 1 }, data);
    // A document with many gradient maps would otherwise grow without bound.
    if (this.lutTextures.size >= 32) {
      const oldest = this.lutTextures.keys().next();
      if (!oldest.done) {
        this.lutTextures.get(oldest.value)?.dispose();
        this.lutTextures.delete(oldest.value);
      }
    }
    this.lutTextures.set(key, texture);
    return texture;
  }

  /** Pattern tile for a fill layer; reused across frames for the same id. */
  patternTexture(
    id: string,
    width: number,
    height: number,
    data: Uint8Array,
  ): Texture | null {
    if (!this.ctx) return null;
    const cached = this.patternTextures.get(id);
    if (cached) return cached;
    const texture = Texture.create(this.ctx.gl, width, height, 'rgba8', 'nearest');
    texture.uploadPixels({ x: 0, y: 0, width, height }, data);
    if (this.patternTextures.size >= 64) {
      const oldest = this.patternTextures.keys().next();
      if (!oldest.done) {
        this.patternTextures.get(oldest.value)?.dispose();
        this.patternTextures.delete(oldest.value);
      }
    }
    this.patternTextures.set(id, texture);
    return texture;
  }

  get totalTextureBytes(): number {
    let total = 0;
    for (const resources of this.docs.values()) total += resources.bytes;
    return total;
  }

  // --- Rendering ---

  /**
   * Composites the whole tree into a pooled buffer. The result stays valid only
   * until the next composite, so this always invalidates the screen cache: every
   * caller that reads pixels back would otherwise leave `renderDocument` holding
   * a buffer that the next walk overwrites with an intermediate stage.
   */
  compositeToTexture(doc: DocumentMeta, options: RenderOptions): Texture {
    const resources = this.resourcesFor(doc);
    const context: CompositeContext = { doc, resources, options };

    resources.buffers.color.releaseAll();
    resources.buffers.coverage.releaseAll();
    this.compositeDirty = true;
    return this.compositeList(doc.layers, null, context).texture;
  }

  /**
   * Composites a sibling list over `seed`, bottom-up. Passing the outer backdrop
   * as the seed is what makes a pass-through group behave as if its children were
   * spliced into the parent list.
   *
   * `owner` is the pooled buffer holding the result, or null when the result is
   * a texture this pass does not own, such as the caller's seed or a layer
   * surface. Handing ownership back is what lets each pass recycle the buffers
   * it consumed, so peak use follows the depth of the tree rather than the
   * number of layers.
   */
  private compositeList(
    layers: readonly Layer[],
    seed: Texture | null,
    context: CompositeContext,
  ): CompositeResult {
    const { color } = context.resources.buffers;
    // The seed is only ever read, and every pass writes into a fresh buffer, so
    // it can serve as the first backdrop without being copied.
    let backdrop: CompositeResult;
    if (seed) {
      backdrop = { texture: seed, owner: null };
    } else {
      const cleared = color.acquire();
      cleared.clear(0, 0, 0, 0);
      backdrop = { texture: cleared.texture, owner: cleared };
    }

    let index = 0;
    while (index < layers.length) {
      const layer = layers[index];
      // A clipping run belongs to the first non-clipping layer below it.
      let end = index + 1;
      while (end < layers.length && layers[end].clipping) end += 1;
      const clipped = layers.slice(index + 1, end);
      index = end;

      if (!layer.visible || layer.opacity <= 0) continue;

      // A clipping base has to have alpha of its own, which only an isolated
      // composite gives, so a group with followers cannot pass through.
      if (clipped.length === 0 && isPassThrough(layer)) {
        // Children blend against the outer backdrop, so continue the same chain.
        const children = this.compositeList(layer.children, backdrop.texture, context);
        // An empty group hands the seed straight back; keeping our own buffer
        // then is what stops it from being recycled while still in use.
        if (children.owner) {
          if (backdrop.owner) color.release(backdrop.owner);
          backdrop = children;
        }
        continue;
      }

      let content = this.layerContent(layer, backdrop.texture, context);
      if (!content) continue;
      if (clipped.length > 0) content = this.compositeClipped(content, clipped, context);
      content = this.withLayerEffects(layer, content, context);

      const target = color.acquire();
      this.renderer.blendLayer(target.framebuffer, backdrop.texture, {
        texture: content.texture,
        mask: this.maskFor(layer, context),
        maskFollows: layer.maskLinked,
        opacity: layer.opacity,
        blendMode: layer.blendMode,
        blendChannels: layer.blendChannels,
        blendIf: layer.blendIf,
        srcMatrix: this.transformFor(layer, context),
      });
      // Both inputs have been read by now, so their buffers can go back.
      if (content.owner) color.release(content.owner);
      if (backdrop.owner) color.release(backdrop.owner);
      backdrop = { texture: target.texture, owner: target };
    }

    return backdrop;
  }

  /**
   * The pixels a layer contributes before its own opacity, mask and blend mode
   * are applied. Adjustment layers derive theirs from the backdrop below them.
   */
  private layerContent(
    layer: Layer,
    backdrop: Texture,
    context: CompositeContext,
  ): CompositeResult | null {
    if (layer.kind === 'group') {
      return this.compositeList(layer.children, null, context);
    }
    if (layer.kind === 'adjustment') {
      const pass = adjustmentPass(this, context.resources, layer.adjustment, backdrop);
      const target = context.resources.buffers.color.acquire();
      this.renderer.applyAdjustment(target.framebuffer, backdrop, null, pass);
      return { texture: target.texture, owner: target };
    }
    if (layer.kind === 'fill') {
      const target = renderFillLayer(
        this,
        context.resources,
        layer.fill,
        context.doc.width,
        context.doc.height,
      );
      return { texture: target.texture, owner: target };
    }
    const override = context.options.overrides?.get(layer.id);
    if (layer.kind === 'smart' && !override) {
      return this.smartContent(layer, context);
    }
    const surface = override ?? context.resources.ensureLayer(layer.id).surface.texture;
    return { texture: surface, owner: null };
  }

  /** Replays smart filters from the preserved original onto a pooled buffer. */
  private smartContent(layer: Layer & { kind: 'smart' }, context: CompositeContext): CompositeResult {
    const resources = context.resources;
    const original = resources.ensureSmartSource(layer.id).texture;
    const filters = layer.smartFilters.filter((entry) => entry.enabled);
    if (filters.length === 0) {
      return { texture: original, owner: null };
    }
    let current: CompositeResult = { texture: original, owner: null };
    for (const entry of filters) {
      const pass = filterPass(this, resources, entry.filter, entry.values, current.texture);
      const target = resources.buffers.color.acquire();
      this.renderer.applyFilter(target.framebuffer, current.texture, null, pass);
      if (current.owner) resources.buffers.color.release(current.owner);
      current = { texture: target.texture, owner: target };
    }
    return current;
  }

  private withLayerEffects(
    layer: Layer,
    content: CompositeResult,
    context: CompositeContext,
  ): CompositeResult {
    const fx = applyLayerEffects(
      this,
      context.resources,
      content.texture,
      layer.effects,
      context.doc.width,
      context.doc.height,
    );
    if (!fx.owner) return content;
    if (content.owner) context.resources.buffers.color.release(content.owner);
    return { texture: fx.texture, owner: fx.owner };
  }

  /**
   * Composites a clipping run on top of its base in isolation, with every
   * member limited to the base's alpha. The caller then blends the whole stack
   * using the base layer's own blend mode and opacity, as Photoshop does.
   */
  private compositeClipped(
    base: CompositeResult,
    clipped: readonly Layer[],
    context: CompositeContext,
  ): CompositeResult {
    const { color, coverage } = context.resources.buffers;
    const baseAlpha = coverage.acquire();
    this.renderer.extractAlpha(baseAlpha.framebuffer, base.texture);

    let stack = base;
    for (const layer of clipped) {
      if (!layer.visible || layer.opacity <= 0) continue;
      let content = this.layerContent(layer, stack.texture, context);
      if (!content) continue;
      content = this.withLayerEffects(layer, content, context);

      // Intersecting the layer's own mask with the base alpha is what clips it.
      const ownMask = this.maskFor(layer, context);
      let mask = baseAlpha.texture;
      let combined: RenderTarget | null = null;
      if (ownMask) {
        combined = coverage.acquire();
        this.renderer.combineMask(combined.framebuffer, ownMask, baseAlpha.texture, INTERSECT_MODE);
        mask = combined.texture;
      }

      const target = color.acquire();
      this.renderer.blendLayer(target.framebuffer, stack.texture, {
        texture: content.texture,
        mask,
        // The clip comes from the base layer's alpha, which does not move when
        // the clipped layer does, so this mask stays in document space.
        maskFollows: false,
        opacity: layer.opacity,
        blendMode: layer.blendMode,
        blendChannels: layer.blendChannels,
        blendIf: layer.blendIf,
        srcMatrix: this.transformFor(layer, context),
      });
      if (combined) coverage.release(combined);
      if (content.owner) color.release(content.owner);
      if (stack.owner) color.release(stack.owner);
      stack = { texture: target.texture, owner: target };
    }

    coverage.release(baseAlpha);
    return stack;
  }

  private maskFor(layer: Layer, context: CompositeContext): Texture | null {
    if (!layer.hasMask || !layer.maskEnabled) return null;
    const override = context.options.maskOverrides?.get(layer.id);
    if (override) return override;
    return context.resources.layer(layer.id)?.mask?.texture ?? null;
  }

  /**
   * Derives the channel view from a finished composite. Several visible channels
   * are multiplied into one coverage texture, so the overlay marks everything
   * that any of them hides.
   */
  private applyChannelView(
    composited: Texture,
    render: RenderOptions,
    resources: DocumentResources,
  ): Texture {
    const options = render.channels!;
    const { color, coverage } = resources.buffers;
    const channelTexture = (id: string): Texture | null =>
      render.channelOverrides?.get(id) ?? resources.channel(id)?.texture ?? null;
    const maskTexture = (layerId: string): Texture | null =>
      render.maskOverrides?.get(layerId) ?? resources.layer(layerId)?.mask?.texture ?? null;

    const single =
      options.greyChannel !== null
        ? channelTexture(options.greyChannel)
        : options.greyMask !== null
          ? maskTexture(options.greyMask)
          : null;

    let overlay = single;
    let combined: RenderTarget | null = null;
    if (!overlay) {
      const sources: Texture[] = [];
      for (const id of options.overlays) {
        const texture = channelTexture(id);
        if (texture) sources.push(texture);
      }
      if (options.overlayMask) {
        const mask = maskTexture(options.overlayMask);
        if (mask) sources.push(mask);
      }
      if (sources.length === 1) {
        overlay = sources[0];
      } else if (sources.length > 1) {
        combined = coverage.acquire();
        this.renderer.copy(combined.framebuffer, sources[0]);
        for (let i = 1; i < sources.length; i++) {
          const next = coverage.acquire();
          this.renderer.combineMask(
            next.framebuffer,
            combined.texture,
            sources[i],
            INTERSECT_MODE,
          );
          coverage.release(combined);
          combined = next;
        }
        overlay = combined.texture;
      }
    }

    const grey =
      options.greyColor >= 0 ? options.greyColor : single && overlay ? GREY_OVERLAY : -1;

    const target = color.acquire();
    this.renderer.drawChannelView(target.framebuffer, composited, {
      grey,
      colorMask: [
        options.colorVisible.r ? 1 : 0,
        options.colorVisible.g ? 1 : 0,
        options.colorVisible.b ? 1 : 0,
      ],
      overlay,
      overlayColor: options.overlayColor,
    });
    if (combined) coverage.release(combined);
    return target.texture;
  }

  private transformFor(layer: Layer, context: CompositeContext): Mat3 | null {
    const offset = layer.contentOffset ?? { x: 0, y: 0 };
    const offsetSrc = contentOffsetSrcMatrix(offset, context.doc.width, context.doc.height);
    const transform = context.options.transform;
    const liveSrc = transform && transform.layerId === layer.id ? transform.srcMatrix : null;
    return composeSrcMatrices(offsetSrc, liveSrc);
  }

  renderDocument(doc: DocumentMeta, options: RenderOptions): void {
    if (!this.ctx) return;
    const resources = this.resourcesFor(doc);

    const cached =
      !this.compositeDirty && this.compositeCache?.docId === doc.id
        ? this.compositeCache.texture
        : null;
    const composited = cached ?? this.compositeToTexture(doc, options);
    this.compositeCache = { docId: doc.id, texture: composited };
    this.compositeDirty = false;

    const shown = options.channels
      ? this.applyChannelView(composited, options, resources)
      : composited;

    const canvas = this.canvasSize;
    const viewMatrix = buildViewMatrix(options.view, canvas, doc.width, doc.height);
    this.renderer.drawToScreen(
      shown,
      viewMatrix,
      options.view.zoom,
      this.ctx.canvas.width,
      this.ctx.canvas.height,
      CHECKER_SIZE_CSS * this.dpr,
    );

    if (options.showSelection !== false && !resources.selection.isEmpty) {
      // Keep the outline one screen pixel wide however far the view is zoomed.
      const scale = Math.min(1, options.view.zoom);
      this.renderer.drawSelectionOverlay(
        resources.selection.texture,
        viewMatrix,
        1 / (doc.width * scale),
        1 / (doc.height * scale),
        options.antsPhase ?? 0,
        ANTS_DASH_CSS * this.dpr,
        options.selectionTransform ?? null,
      );
    }
  }

  /** Clears the canvas, used when no document is open. */
  clearCanvas(): void {
    if (!this.ctx) return;
    const { gl } = this.ctx;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);
    gl.clearColor(0.13, 0.13, 0.14, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  /**
   * Small preview of a layer for the layers panel. The downscale happens on the
   * GPU, so only a few hundred pixels ever cross back to the CPU.
   */
  layerThumbnail(doc: DocumentMeta, layerId: string, maxSize = 40): ImageData | null {
    if (!this.ctx) return null;
    const resources = this.resourcesFor(doc);
    // Groups and adjustment layers own no pixels; their rows draw an icon.
    const surface = resources.layer(layerId)?.surface.texture;
    if (!surface) return null;

    const scale = maxSize / Math.max(doc.width, doc.height);
    const width = Math.max(1, Math.round(doc.width * scale));
    const height = Math.max(1, Math.round(doc.height * scale));

    if (!this.thumbTarget) {
      this.thumbTarget = new RenderTarget(this.ctx.gl, width, height, 'rgba8', 'linear');
    }
    this.thumbTarget.resize(width, height);
    this.renderer.copy(this.thumbTarget.framebuffer, surface);
    const data = this.thumbTarget.texture.readPixels();
    return new ImageData(new Uint8ClampedArray(data), width, height);
  }

  maskThumbnail(doc: DocumentMeta, layerId: string, maxSize = 40): ImageData | null {
    const mask = this.resourcesFor(doc).layer(layerId)?.mask;
    return mask ? this.coverageThumbnail(doc, mask.texture, maxSize) : null;
  }

  channelThumbnail(doc: DocumentMeta, channelId: string, maxSize = 40): ImageData | null {
    const channel = this.resourcesFor(doc).channel(channelId);
    return channel ? this.coverageThumbnail(doc, channel.texture, maxSize) : null;
  }

  /** Greyscale preview of a single-channel texture: a mask or an alpha channel. */
  private coverageThumbnail(doc: DocumentMeta, source: Texture, maxSize: number): ImageData | null {
    if (!this.ctx) return null;
    const scale = maxSize / Math.max(doc.width, doc.height);
    const width = Math.max(1, Math.round(doc.width * scale));
    const height = Math.max(1, Math.round(doc.height * scale));

    if (!this.coverageThumbTarget) {
      this.coverageThumbTarget = new RenderTarget(this.ctx.gl, width, height, 'r8', 'linear');
    }
    this.coverageThumbTarget.resize(width, height);
    this.renderer.copy(this.coverageThumbTarget.framebuffer, source);
    const coverage = this.coverageThumbTarget.texture.readPixels();

    const rgba = new Uint8ClampedArray(width * height * 4);
    for (let i = 0; i < coverage.length; i += 1) {
      const value = coverage[i];
      rgba[i * 4] = value;
      rgba[i * 4 + 1] = value;
      rgba[i * 4 + 2] = value;
      rgba[i * 4 + 3] = 255;
    }
    return new ImageData(rgba, width, height);
  }

  /** Downscaled preview of the whole document, for the navigator panel. */
  compositeThumbnail(doc: DocumentMeta, maxSize = 160): ImageData | null {
    if (!this.ctx) return null;
    const composited = this.compositeToTexture(doc, {
      view: { zoom: 1, centerX: 0, centerY: 0, rotation: 0 },
      showSelection: false,
    });

    const scale = Math.min(1, maxSize / Math.max(doc.width, doc.height));
    const width = Math.max(1, Math.round(doc.width * scale));
    const height = Math.max(1, Math.round(doc.height * scale));

    if (!this.previewTarget) {
      this.previewTarget = new RenderTarget(this.ctx.gl, width, height, 'rgba8', 'linear');
    }
    this.previewTarget.resize(width, height);
    this.renderer.copy(this.previewTarget.framebuffer, composited);
    const data = this.previewTarget.texture.readPixels();
    return new ImageData(new Uint8ClampedArray(data), width, height);
  }

  /** Flattens the document to straight-alpha RGBA rows, top row first. */
  flatten(doc: DocumentMeta): { width: number; height: number; data: Uint8Array } {
    const texture = this.compositeToTexture(doc, {
      view: { zoom: 1, centerX: 0, centerY: 0, rotation: 0 },
      showSelection: false,
    });
    const data = texture.readPixels();
    return { width: doc.width, height: doc.height, data };
  }

  // --- Snapshots and history ---

  snapshotSurface(doc: DocumentMeta, layerId: string): FullSnapshot {
    const resources = this.resourcesFor(doc);
    const layer = resources.ensureLayer(layerId);
    return {
      width: layer.surface.width,
      height: layer.surface.height,
      data: layer.surface.readPixels(),
    };
  }

  /**
   * Tightest rectangle around a layer's non-transparent pixels, which is what a
   * transform box hugs and what aligning layers measures. `null` when the layer
   * is empty or has no surface of its own, as a group does.
   */
  contentBounds(doc: DocumentMeta, layerId: string): Rect | null {
    const layer = this.resourcesFor(doc).layer(layerId);
    if (!layer) return null;

    const { width, height } = layer.surface;
    const data = layer.surface.readPixels();
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        if (data[(y * width + x) * 4 + 3] === 0) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) return null;
    const offset = findLayerById(doc.layers, layerId)?.contentOffset ?? { x: 0, y: 0 };
    return {
      x: minX + offset.x,
      y: minY + offset.y,
      width: maxX - minX + 1,
      height: maxY - minY + 1,
    };
  }

  snapshotRegion(doc: DocumentMeta, layerId: string, rect: Rect): Uint8Array {
    const resources = this.resourcesFor(doc);
    return resources.ensureLayer(layerId).surface.readPixels(rect);
  }

  snapshotLayerMask(doc: DocumentMeta, layerId: string): FullSnapshot | null {
    const resources = this.resourcesFor(doc);
    const mask = resources.layer(layerId)?.mask;
    if (!mask) return null;
    return { width: mask.width, height: mask.height, data: mask.texture.readPixels() };
  }

  snapshotChannel(doc: DocumentMeta, channelId: string): FullSnapshot | null {
    const channel = this.resourcesFor(doc).channel(channelId);
    if (!channel) return null;
    return { width: channel.width, height: channel.height, data: channel.texture.readPixels() };
  }

  snapshotSelection(doc: DocumentMeta): FullSnapshot | null {
    const resources = this.resourcesFor(doc);
    if (resources.selection.isEmpty) return null;
    return {
      width: resources.selection.width,
      height: resources.selection.height,
      data: resources.selection.snapshot(),
    };
  }

  /**
   * Restores the `before` (undo) or `after` (redo) side of every op. Undo walks
   * the ops backwards so that metadata is restored before the pixels it refers to.
   */
  applyHistoryOps(doc: DocumentMeta, ops: EngineHistoryOp[], direction: 'undo' | 'redo'): void {
    const resources = this.resourcesFor(doc);
    const ordered = direction === 'undo' ? [...ops].reverse() : ops;

    for (const op of ordered) {
      const side = direction === 'undo' ? 'before' : 'after';
      switch (op.kind) {
        case 'pixels': {
          const snapshot = op[side];
          resources.ensureLayer(op.layerId).surface.writePixels(snapshot.rect, snapshot.data);
          break;
        }
        case 'surface': {
          const snapshot = op[side];
          if (!snapshot) {
            resources.deleteLayer(op.layerId);
            break;
          }
          const layer = resources.ensureLayer(op.layerId);
          layer.surface.resize(snapshot.width, snapshot.height);
          if (snapshot.data) {
            layer.surface.writePixels(
              { x: 0, y: 0, width: snapshot.width, height: snapshot.height },
              snapshot.data,
            );
          } else {
            const [r, g, b, a] = snapshot.fill ?? [0, 0, 0, 0];
            layer.surface.target.clear(r, g, b, a);
          }
          break;
        }
        case 'maskPixels': {
          const snapshot = op[side];
          resources
            .ensureMask(op.layerId)
            .texture.uploadPixels(snapshot.rect, snapshot.data);
          break;
        }
        case 'layerMask': {
          const snapshot = op[side];
          if (!snapshot) {
            resources.removeMask(op.layerId);
            break;
          }
          const mask = resources.ensureMask(op.layerId);
          mask.resize(snapshot.width, snapshot.height);
          if (snapshot.data) {
            mask.texture.uploadPixels(
              { x: 0, y: 0, width: snapshot.width, height: snapshot.height },
              snapshot.data,
            );
          } else {
            const [r, g, b, a] = snapshot.fill ?? [1, 1, 1, 1];
            mask.clear(r, g, b, a);
          }
          break;
        }
        case 'channelPixels': {
          const snapshot = op[side];
          resources.ensureChannel(op.channelId).texture.uploadPixels(snapshot.rect, snapshot.data);
          break;
        }
        case 'channel': {
          const snapshot = op[side];
          if (!snapshot) {
            resources.removeChannel(op.channelId);
            break;
          }
          const channel = resources.ensureChannel(op.channelId);
          channel.resize(snapshot.width, snapshot.height);
          if (snapshot.data) {
            channel.texture.uploadPixels(
              { x: 0, y: 0, width: snapshot.width, height: snapshot.height },
              snapshot.data,
            );
          } else {
            const [r, g, b, a] = snapshot.fill ?? [0, 0, 0, 0];
            channel.clear(r, g, b, a);
          }
          break;
        }
        case 'selection': {
          resources.selection.restore(op[side]?.data ?? null);
          break;
        }
        case 'meta': {
          this.callbacks.onMetaRestore?.(doc.id, op[side]);
          break;
        }
      }
    }
    this.invalidate();
  }

  private discardResources(): void {
    for (const resources of this.docs.values()) resources.dispose();
    this.docs.clear();
    for (const texture of this.tipTextures.values()) texture.dispose();
    this.tipTextures.clear();
    for (const texture of this.lutTextures.values()) texture.dispose();
    this.lutTextures.clear();
    for (const texture of this.patternTextures.values()) texture.dispose();
    this.patternTextures.clear();
    this.thumbTarget?.dispose();
    this.thumbTarget = null;
    this.coverageThumbTarget?.dispose();
    this.coverageThumbTarget = null;
    this.previewTarget?.dispose();
    this.previewTarget = null;
    this.rendererInstance?.dispose();
    this.compositeCache = null;
    this.compositeDirty = true;
  }

  private readonly handleContextLost = (event: Event): void => {
    // Preventing the default is what makes the browser send `restored` later.
    event.preventDefault();
    this.discardResources();
    this.rendererInstance = null;
    this.ctx = null;
    this.callbacks.onContextLost?.();
  };

  private readonly handleContextRestored = (): void => {
    this.callbacks.onContextRestored?.();
  };
}

let engineInstance: Engine | null = null;

export function getEngine(): Engine {
  if (!engineInstance) engineInstance = new Engine();
  return engineInstance;
}
